import { z } from "zod";
import {
  AgentRunState,
  ClaudeModel,
  DecisionCategory,
  DecisionState,
  DeliveryMode,
  EffortLevel,
  OwnerTaskPlace,
  IdeaMaturity,
  Lane,
  NodeState,
  Platform,
  ProjectLifecycle,
  Role,
  WorkItemState,
  WorkOrderState,
} from "./enums";
import { AuthMode } from "./run";

const Id = z.string().min(1).max(64);
const Timestamp = z.string();
const Hash = z.string().regex(/^sha256:[0-9a-f]{64}$/, "erwartet sha256:<64 hex>");

export const User = z.object({
  id: Id,
  displayName: z.string(),
  role: Role,
  createdAt: Timestamp,
});
export type User = z.infer<typeof User>;

export const Idea = z.object({
  id: Id,
  title: z.string(),
  body: z.string(),
  tags: z.array(z.string()),
  maturity: IdeaMaturity,
  origin: z.enum(["web", "mobile", "taskflow"]),
  promotedProjectId: Id.nullable(),
  createdBy: Id,
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Idea = z.infer<typeof Idea>;

/** Ein prüfbares Kriterium aus „Fertig heißt“; `metAt` ist der Zeitpunkt, an dem es erfüllt wurde. */
export const DoneCriterion = z.object({
  text: z.string(),
  met: z.boolean(),
  metAt: Timestamp.nullable(),
});
export type DoneCriterion = z.infer<typeof DoneCriterion>;

export const Project = z.object({
  id: Id,
  title: z.string(),
  goal: z.string(),
  platform: Platform,
  profileKey: z.string(),
  origin: z.enum(["new", "idea", "import"]),
  ideaId: Id.nullable(),
  /** GitHub-Repo `owner/name` (ADR-015) und sein Standard-Branch. */
  repoFullName: z.string().nullable(),
  repoDefaultBranch: z.string().nullable(),
  lifecycle: ProjectLifecycle,
  /** Im Fokus: höchstens drei Projekte gleichzeitig (packages/domain/src/focus.ts). */
  focus: z.boolean(),
  /** „Fertig heißt“: Kriterien für die erste fertige Version, und was bewusst später kommt. */
  doneCriteria: z.array(DoneCriterion),
  later: z.array(z.string()),
  /** Auslieferungsweg und, für `manual`, die Schritte der Checkliste nach dem Mergen. */
  deliveryMode: DeliveryMode,
  deliverySteps: z.array(z.string()),
  /** Ausrollen per Knopf über einen Helfer auf dem App-Server; beide `null`, solange nichts eingestellt ist. */
  deployAgentId: z.string().nullable(),
  deployApp: z.string().nullable(),
  createdBy: Id,
  createdAt: Timestamp,
});
export type Project = z.infer<typeof Project>;

export const SpecContent = z.strictObject({
  problem: z.string().min(1).max(4000),
  audience: z.string().min(1).max(2000),
  mustHaves: z.array(z.string().min(1).max(500)).min(1).max(50),
  nonGoals: z.array(z.string().min(1).max(500)).max(50),
  designNotes: z.string().max(4000),
  acceptance: z.array(z.string().min(1).max(500)).min(1).max(50),
});
export type SpecContent = z.infer<typeof SpecContent>;

export const PlanItemInput = z.strictObject({
  key: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "nur Kleinbuchstaben, Ziffern und Bindestrich"),
  lane: Lane,
  title: z.string().min(1).max(200),
  acceptanceCriteria: z.array(z.string().min(1).max(500)).min(1).max(20),
  dependsOn: z.array(z.string()).max(30),
  providerProfile: z.string().max(40).default("mock"),
  /** Modell und Aufwand für diese Aufgabe; ohne Angabe gilt der Standard unter Agenten. */
  model: ClaudeModel.optional(),
  effort: EffortLevel.optional(),
});
export type PlanItemInput = z.infer<typeof PlanItemInput>;

export const PlanContent = z.strictObject({
  items: z.array(PlanItemInput).min(1).max(200),
});
export type PlanContent = z.infer<typeof PlanContent>;

export const WorkOrder = z.object({
  id: Id,
  projectId: Id,
  title: z.string(),
  kind: z.enum(["new_app", "feature", "bugfix", "audit", "research", "release"]),
  origin: z.enum(["szyos", "taskflow"]),
  state: WorkOrderState,
  resumeState: WorkOrderState.nullable(),
  stateReason: z.string().nullable(),
  specRevision: z.number().int().nonnegative(),
  specHash: Hash.nullable(),
  planRevision: z.number().int().nonnegative(),
  planHash: Hash.nullable(),
  budgetUsd: z.number().nonnegative(),
  /** Mensch, der den Auftrag gestartet hat; entscheidet über den Auth-Modus (ADR-016). */
  commissionedBy: Id.nullable(),
  /** Bei `PAUSED_PROVIDER`: Zeitpunkt, ab dem der Auftrag selbst weiterläuft. */
  pausedUntil: Timestamp.nullable(),
  /** Gemeinsamer Branch und Pull Request des Auftrags (QA-07). */
  gitBranch: z.string().nullable(),
  gitPrUrl: z.string().nullable(),
  /** Gemergter Stand, der erst mit dem Ausrollen des Büros ausgeliefert ist (nur das szyOS-Repo selbst). */
  awaitingRolloutSha: z.string().nullable(),
  /** Vom Owner angehalten; keine neuen Läufe bis „Fortsetzen“. */
  heldAt: Timestamp.nullable(),
  priority: z.number().int(),
  version: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type WorkOrder = z.infer<typeof WorkOrder>;

export const WorkItem = z.object({
  id: Id,
  workOrderId: Id,
  key: z.string(),
  lane: Lane,
  title: z.string(),
  acceptanceCriteria: z.array(z.string()),
  dependsOn: z.array(z.string()),
  providerProfile: z.string(),
  state: WorkItemState,
  attempts: z.number().int().nonnegative(),
  evidence: z.array(z.string()),
  /** Rückmeldung aus Prüfung oder Review für den nächsten Versuch (QA-07). */
  feedback: z.array(z.string()),
  reworkRound: z.number().int().nonnegative(),
});
export type WorkItem = z.infer<typeof WorkItem>;

export const Usage = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costUsdMeasured: z.number().nonnegative(),
  costUsdEstimated: z.number().nonnegative(),
  quotaUnits: z.number().nonnegative(),
  /** Abo-Läufe: geschätzter API-Gegenwert, zählt nie auf Budget oder Kostensumme (docs/03 §8). */
  apiEquivalentUsd: z.number().nonnegative().optional(),
});
export type Usage = z.infer<typeof Usage>;

export const AgentRun = z.object({
  id: Id,
  workItemId: Id,
  workOrderId: Id,
  adapter: z.string(),
  authMode: AuthMode,
  nodeId: Id.nullable(),
  attempt: z.number().int().positive(),
  state: AgentRunState,
  runSpecHash: Hash,
  usage: Usage,
  resultSummary: z.string().nullable(),
  failureReason: z.string().nullable(),
  queuedAt: Timestamp,
  startedAt: Timestamp.nullable(),
  finishedAt: Timestamp.nullable(),
});
export type AgentRun = z.infer<typeof AgentRun>;

export const DecisionOption = z.object({
  id: z.string(),
  label: z.string(),
  title: z.string(),
  pros: z.array(z.string()),
  cons: z.array(z.string()),
  notes: z.array(z.string()),
  requiresHold: z.boolean(),
});
export type DecisionOption = z.infer<typeof DecisionOption>;

export const DecisionBinding = z.object({
  specHash: Hash.nullable(),
  planHash: Hash.nullable(),
  commitSha: z.string().nullable(),
  policyRevision: z.number().int().nonnegative(),
});
export type DecisionBinding = z.infer<typeof DecisionBinding>;

/** Stichwortübersicht der Änderungen eines Pull Requests für Abnahme und Merge-Freigabe. */
export const ChangeSummary = z.object({
  files: z.number().int().nonnegative(),
  additions: z.number().int().nonnegative(),
  deletions: z.number().int().nonnegative(),
  added: z.array(z.string()),
  modified: z.array(z.string()),
  removed: z.array(z.string()),
  renamed: z.array(z.string()),
  truncated: z.boolean(),
  tasks: z.array(z.string()),
  /** Geänderte Zeilen ohne Tests und Doku; fehlt bei älteren Übersichten. */
  productLines: z.number().int().nonnegative().optional(),
});
export type ChangeSummary = z.infer<typeof ChangeSummary>;

export const Decision = z.object({
  id: Id,
  workOrderId: Id.nullable(),
  workItemId: Id.nullable(),
  category: DecisionCategory,
  question: z.string(),
  context: z.string(),
  blocks: z.string().nullable(),
  options: z.array(DecisionOption).min(2).max(3),
  /** Schritte, die vor der Option `delivered` abgehakt sein müssen (Auslieferung von Hand). */
  checklist: z.array(z.string()),
  /** Was der Pull Request ändert; fehlt ohne Repo oder wenn GitHub nicht erreichbar war. */
  changes: ChangeSummary.nullable(),
  recommendedOptionId: z.string().nullable(),
  requiredRole: Role,
  binding: DecisionBinding,
  state: DecisionState,
  resolvedOptionId: z.string().nullable(),
  resolvedBy: Id.nullable(),
  resolvedAt: Timestamp.nullable(),
  expiresAt: Timestamp,
  createdAt: Timestamp,
});
export type Decision = z.infer<typeof Decision>;

export const SzyNode = z.object({
  id: Id,
  name: z.string(),
  state: NodeState,
  capabilities: z.array(z.string()),
  /** Zuletzt gemeldete eingerichtete Auth-Modi, z. B. „Abo-Modus eingerichtet“. */
  authModes: z.array(z.string()),
  lastHeartbeatAt: Timestamp.nullable(),
  version: z.string(),
  createdAt: Timestamp,
});
export type SzyNode = z.infer<typeof SzyNode>;

export const DomainEvent = z.object({
  schemaVersion: z.literal("1.1"),
  eventId: Id,
  seq: z.number().int().positive(),
  aggregateType: z.string(),
  aggregateId: Id,
  eventType: z.string(),
  occurredAt: Timestamp,
  actor: z.object({ type: z.enum(["user", "service", "node"]), id: z.string() }),
  correlationId: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
});
export type DomainEvent = z.infer<typeof DomainEvent>;

/** Datei, in die ein Studio-Lauf sein Ergebnis schreibt. */
export const STUDIO_OUTPUT_FILE = "szyos-output.json";

/**
 * Antwort der KI im Planungsstudio (M5-01, M5-02). Nachsichtig gelesen: unbekannte Felder werden
 * verworfen, Pflicht ist nur die Zusammenfassung. Briefing- und Planentwürfe sind nur Vorschläge.
 */
export const StudioReply = z.object({
  summary: z.string().min(1).max(4000),
  questions: z.array(z.string().min(1).max(600)).max(6).default([]),
  audience: z.string().max(600).optional(),
  effortEstimate: z.string().max(400).optional(),
  similarProjects: z.array(z.string().max(200)).max(5).optional(),
  // Nachsichtig: zusätzliche Felder der KI werden verworfen statt die ganze Antwort abzulehnen.
  specDraft: z.object(SpecContent.shape).optional(),
  planDraft: z
    .object({
      items: z
        .array(
          // Begründung der KI für Modell und Aufwand; landet in der Beratung, nicht im Plan.
          z.object(PlanItemInput.shape).extend({ modelReason: z.string().max(400).optional() }),
        )
        .min(1)
        .max(60),
    })
    .optional(),
});
export type StudioReply = z.infer<typeof StudioReply>;

/**
 * Beratung zu einer Planaufgabe: Modell und Aufwand nach KI-Vorschlag und Leitplanken, mit Begründung.
 * `source` sagt, wer entschieden hat: die KI (Planerin, ggf. von der Prüferin korrigiert) oder die Regeln,
 * wenn die KI nichts Brauchbares geliefert hat.
 */
export const ItemAdvice = z.object({
  model: ClaudeModel,
  effort: EffortLevel,
  recommendedEffort: EffortLevel.nullable(),
  source: z.enum(["ki", "regeln"]),
  reasons: z.array(z.string().max(400)).max(12),
  review: z
    .object({
      verdict: z.enum(["passt", "zu_gross", "zu_klein"]),
      note: z.string().max(400),
      removedCriteria: z.array(z.string().max(500)).max(20),
    })
    .optional(),
});
export type ItemAdvice = z.infer<typeof ItemAdvice>;

/** Zweite Prüfung eines Planvorschlags durch eine sparsame KI auf Verhältnismäßigkeit. */
export const PlanReview = z.object({
  state: z.enum(["pending", "done", "failed"]),
  runId: z.string().optional(),
  summary: z.string().max(1000).optional(),
  error: z.string().max(400).optional(),
});
export type PlanReview = z.infer<typeof PlanReview>;

/** Planvorschlag im Studio: der Plan selbst plus Beratung je Aufgabe und Ergebnis der Prüfung. */
export const PlanProposal = PlanContent.extend({
  advice: z.record(z.string(), ItemAdvice).optional(),
  review: PlanReview.optional(),
});
export type PlanProposal = z.infer<typeof PlanProposal>;

export const StudioTurn = z.object({
  role: z.enum(["owner", "ai"]),
  at: Timestamp,
  text: z.string().optional(),
  reply: StudioReply.optional(),
});
export type StudioTurn = z.infer<typeof StudioTurn>;

export const StudioSession = z.object({
  id: Id,
  kind: z.enum(["clarify", "plan"]),
  ideaId: Id.nullable(),
  workOrderId: Id.nullable(),
  state: z.enum(["waiting_ai", "waiting_owner", "done", "failed"]),
  turns: z.array(StudioTurn),
  proposal: z.unknown().nullable(),
  error: z.string().nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type StudioSession = z.infer<typeof StudioSession>;

/** Ein Schritt, den nur der Owner außerhalb von szyOS erledigen kann (UX-12). */
export const OwnerTask = z.object({
  id: Id,
  projectId: Id.nullable(),
  /** Auftrag, der erst mit diesem Schritt fertig ist; `null` bei frei angelegten Einträgen. */
  workOrderId: Id.nullable(),
  title: z.string(),
  place: OwnerTaskPlace,
  urgent: z.boolean(),
  note: z.string(),
  steps: z.array(z.string()),
  /** Woran szyOS die Erledigung selbst erkennt; `null`, wenn nur Abhaken schließt. */
  autoCheckLabel: z.string().nullable(),
  doneAt: Timestamp.nullable(),
  createdAt: Timestamp,
});
export type OwnerTask = z.infer<typeof OwnerTask>;
